/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Deal, DealProposal } from '@/services/requests/housien-services/DealsService';
import { acceptedOfferValue, daysSince } from './deal-state';

/**
 * The latest offer on the attendance, whoever made it.
 *
 * Offers arrive oldest first, but an attendance that had one corrected
 * can have them out of order, so the newest is found by timestamp rather
 * than by position.
 *
 * @param deal - The attendance.
 * @returns The most recent offer, or undefined when none was recorded.
 */
export function latestProposal(deal: Deal): DealProposal | undefined {
  return (deal.proposals ?? [])
    .slice()
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .at(-1);
}

export interface DealValues {
  /** What the listing asks. */
  asking: number | null;
  /** The figure on the table: the accepted offer, else the latest one. */
  standing: number | null;
  standingSide: 'client' | 'owner' | null;
  /** Whole days since the standing offer was made. */
  standingAgeDays: number | null;
  /** Asking minus standing, when both exist and the offer is under. */
  gap: number | null;
  /** The gap as a percentage of the asking price. */
  gapPercent: number | null;
  /** Standing offer times the commission rate, when a rate is known. */
  commission: number | null;
  /** The rate the commission was figured at. */
  commissionPercent: number | null;
  /** Whether that rate came off the agency default rather than the deal. */
  commissionFromDefault: boolean;
  /** Whole days since the attendance was opened. */
  openDays: number | null;
}

/**
 * Resolves the gap (asking minus standing) when the offer is below the asking price.
 */
function computeGap(asking: number | null, standing: number | null): { gap: number | null; gapPercent: number | null } {
  if (asking == null || standing == null || standing >= asking) return { gap: null, gapPercent: null };
  return {
    gap: asking - standing,
    gapPercent: ((asking - standing) / asking) * 100,
  };
}

/**
 * Resolves the commission figure: deal-level rate first, agency sale default fallback.
 */
function resolveCommission(
  deal: Deal,
  standing: number | null,
  defaultSalePercent: number | null,
): { commission: number | null; commissionPercent: number | null; commissionFromDefault: boolean } {
  const ownRate = deal.commission?.total_percentage ?? null;
  const fallbackRate = deal.deal_type === 'sale' ? defaultSalePercent : null;
  const rate = ownRate ?? fallbackRate;
  return {
    commission: rate != null && standing != null ? (standing * rate) / 100 : null,
    commissionPercent: rate,
    commissionFromDefault: ownRate == null && rate != null,
  };
}

/**
 * Every figure the attendance's value band reports.
 *
 * The commission is an estimate and says so: an attendance carries its
 * own rate only once someone has set one, so until then it is figured at
 * the agency's default sale rate — and `commissionFromDefault` is what
 * lets the band admit that rather than present a guess as the deal's own
 * number. A rental attendance with no rate of its own gets none, because
 * the sale default does not describe it.
 *
 * @param deal - The attendance.
 * @param defaultSalePercent - The agency's default sale commission rate.
 * @returns The figures, each null when nothing states it.
 */
export function dealValues(deal: Deal, defaultSalePercent: number | null): DealValues {
  const accepted = acceptedOfferValue(deal);
  const latest = latestProposal(deal);
  const standing = accepted ?? latest?.value ?? null;
  const asking = deal.reference_value ?? null;

  return {
    asking,
    standing,
    standingSide: latest?.side ?? null,
    standingAgeDays: daysSince(latest?.created_at),
    ...computeGap(asking, standing),
    ...resolveCommission(deal, standing, defaultSalePercent),
    openDays: daysSince(deal.created_at),
  };
}
